import * as React from 'react';
import { AppProviderDispatch, AppContext } from '../shared/app-context';
import useRestService from '../shared/rest-service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PushCampaignOverview, PushDestination, PushHistoryOverview, PushOverview } from '../models/segments';
import { TableSortState } from '../models/tableSortState';

export function useGetPushDestinations() {
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { fetchWrapper } = useRestService();

  return useQuery<PushDestination[], Error>(['push-destinations', appState.state.clientId], () => {
    return fetchWrapper(`api/${appState.state.clientId}/push-destinations`);
  });
}

export function useGetCampaignTerm(pushDestinationId: string, clientId: string) {
  const { fetchWrapper } = useRestService();

  return useQuery<string, Error>(
    ['campaign-term', clientId, pushDestinationId],
    () => {
      return fetchWrapper(
        `api/${clientId}/push-destinations/${pushDestinationId}/campaign-term`,
        { method: 'GET' },
        'text'
      );
    },
    {
      enabled: !!pushDestinationId
    }
  );
}

export function useGetCampaignList(pushDestinationId: number, tableSort: TableSortState, searchValue: string) {
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { fetchWrapper } = useRestService();

  let query =
    `Page=${tableSort.page}` +
    `&PageSize=${tableSort.pageSize}` +
    `&Sort=${tableSort.orderColumn}` +
    `&SortDirection=${tableSort.orderDirection}`;

  if (searchValue) {
    query += `&SearchTerm=${searchValue}`;
  }

  return useQuery<PushCampaignOverview, Error>(
    ['campaign-list', appState.state.clientId, pushDestinationId, tableSort, searchValue],
    () => {
      return fetchWrapper(`api/${appState.state.clientId}/push-destinations/${pushDestinationId}/campaigns?${query}`);
    },
    { enabled: !!pushDestinationId }
  );
}

export function usePushSegment() {
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { fetchWrapper } = useRestService();
  const queryClient = useQueryClient();

  return useMutation<
    { message: string; success: boolean },
    Error,
    { pushDestinationId: number; campaignId: string; campaignName: string; segmentId: string; isOneTime: boolean }
  >(
    (pushDetails) => {
      const { pushDestinationId, campaignId, campaignName, segmentId, isOneTime } = pushDetails;
      return fetchWrapper(
        `api/${appState.state.clientId}/push-destinations/${pushDestinationId}/campaigns/${campaignId}/push/${segmentId}`,
        {
          method: 'POST',
          body: JSON.stringify({
            campaignName: campaignName,
            isOneTime: isOneTime
          })
        }
      );
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['push-history'])
    }
  );
}

export function useGetSegmentPushHistory(segmentId: string, tableSort: TableSortState, searchValue: string) {
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { fetchWrapper } = useRestService();

  let query =
    `Page=${tableSort.page}` +
    `&PageSize=${tableSort.pageSize}` +
    `&Sort=${tableSort.orderColumn}` +
    `&SortDirection=${tableSort.orderDirection}`;

  if (searchValue) {
    query += `&SearchTerm=${searchValue}`;
  }

  return useQuery<PushHistoryOverview, Error>(
    ['push-history', appState.state.clientId, segmentId, tableSort, searchValue],
    () => {
      return fetchWrapper(`api/${appState.state.clientId}/segments/${segmentId}/push-history?${query}`);
    },
    { enabled: !!segmentId }
  );
}

export function useGetPushRuns(segmentId: string, pushId: string, tableSort: TableSortState, searchValue: string) {
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { fetchWrapper } = useRestService();

  let query =
    `Page=${tableSort.page}` +
    `&PageSize=${tableSort.pageSize}` +
    `&Sort=${tableSort.orderColumn}` +
    `&SortDirection=${tableSort.orderDirection}`;

  if (searchValue) {
    query += `&SearchTerm=${searchValue}`;
  }

  return useQuery<PushOverview, Error>(
    ['push-runs', appState.state.clientId, pushId, segmentId, tableSort, searchValue],
    () => {
      return fetchWrapper(`api/${appState.state.clientId}/segments/${segmentId}/push-history/${pushId}?${query}`);
    },
    { enabled: !!segmentId && !!pushId }
  );
}

export function useStopAutomaticPush() {
  const { fetchWrapper } = useRestService();
  const queryClient = useQueryClient();

  return useMutation<
    { message: string; success: boolean },
    Error,
    { segmentId: string; pushId: string; clientId: string }
  >(
    (pushDetails) => {
      const { segmentId, pushId, clientId } = pushDetails;
      return fetchWrapper(`api/${clientId}/segments/${segmentId}/push-history/${pushId}/stop`, {
        method: 'PUT'
      });
    },
    {
      onSuccess: (results) => {
        if (results.success) {
          queryClient.invalidateQueries(['push-runs']);
        }
      }
    }
  );
}
